import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSquads, getActiveSessions } from '../../services/api';
import socketService from '../../services/socket';
import { getToken } from '../../services/api';
import { shouldUseMobileUI } from '../../utils/deviceDetection';
import Settings from '../Settings/Settings';
import MobileNav from '../Mobile/MobileNav';
import './Dashboard.css';

function Dashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [squads, setSquads] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [isMobile] = useState(shouldUseMobileUI());

  useEffect(() => {
    // Connect to Socket.io
    const token = getToken();
    if (token) {
      socketService.connect(token);
    }

    // Load initial data
    loadData();

    return () => {
      socketService.disconnect();
    };
  }, []);

  const loadData = async () => {
    try {
      const [squadsData, sessionsData] = await Promise.all([
        getSquads(),
        getActiveSessions(),
      ]);

      setSquads(squadsData.squads || []);
      setActiveSessions(sessionsData.sessions || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard loading">
        <div className="loading-spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* Mobile Navigation */}
      {isMobile && (
        <MobileNav
          isOpen={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
          user={user}
          onLogout={onLogout}
        />
      )}

      <header className="dashboard-header">
        <div className="header-left">
          <div className="header-logo">
            <img src="/fpstak-logo.png" alt="FPSTAK" className="dashboard-logo" />
            <div>
              <h1>FPSTAK</h1>
              {!isMobile && (
                <p className="welcome-text">Welcome, {user.displayName || user.username}!</p>
              )}
            </div>
          </div>
        </div>
        <div className="header-right">
          {isMobile ? (
            <button 
              className="btn-hamburger" 
              onClick={() => setMobileNavOpen(true)}
              aria-label="Open menu"
            >
              ☰
            </button>
          ) : (
            <>
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowSettings(true)}
                title="Settings"
                style={{ marginRight: '12px' }}
              >
                ⚙️ SETTINGS
              </button>
              <button className="btn btn-secondary" onClick={onLogout}>
                LOGOUT
              </button>
            </>
          )}
        </div>
      </header>

      {/* Settings Modal */}
      <Settings isOpen={showSettings} onClose={() => setShowSettings(false)} />

      <main className="dashboard-content">
        <div className="dashboard-grid">
          {/* Quick Stats */}
          <div className="dashboard-section">
            <h2>TACTICAL OVERVIEW</h2>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">👥</div>
                <div className="stat-value">{squads.length}</div>
                <div className="stat-label">SQUADS</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">🎯</div>
                <div className="stat-value">{activeSessions.length}</div>
                <div className="stat-label">ACTIVE SESSIONS</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">📡</div>
                <div className="stat-value">
                  {socketService.isConnected() ? 'ONLINE' : 'OFFLINE'}
                </div>
                <div className="stat-label">CONNECTION STATUS</div>
              </div>
            </div>
          </div>

          {/* Active Sessions */}
          <div className="dashboard-section">
            <div className="section-header">
              <h2>ACTIVE SESSIONS</h2>
            </div>
            {activeSessions.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📊</div>
                <p>No active sessions</p>
                <small>Start a session to begin tracking</small>
              </div>
            ) : (
              <div className="sessions-list">
                {activeSessions.map((session) => (
                  <div
                    key={session.id}
                    className="session-card card"
                    onClick={() => navigate(`/session/${session.id}`)}
                  >
                    <div className="session-info">
                      <h3>{session.name || 'Unnamed Session'}</h3>
                      <p className="text-muted">{session.squadName}</p>
                      {session.game && <span className="badge">{session.game}</span>}
                    </div>
                    <div className="session-stats">
                      <span className="stat-highlight">{session.screenshotCount}</span> screenshots
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Squads */}
          <div className="dashboard-section">
            <div className="section-header">
              <h2>YOUR SQUADS</h2>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => navigate('/squads')}
              >
                MANAGE SQUADS
              </button>
            </div>
            {squads.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">👥</div>
                <p>You're not in any squads yet</p>
                <button
                  className="btn btn-primary mt-2"
                  onClick={() => navigate('/squads')}
                >
                  CREATE YOUR FIRST SQUAD
                </button>
              </div>
            ) : (
              <div className="squads-grid">
                {squads.map((squad) => (
                  <div
                    key={squad.id}
                    className="squad-card card"
                    onClick={() => navigate('/squads')}
                  >
                    <h3>{squad.name}</h3>
                    <p className="text-muted">{squad.memberCount} members</p>
                    <span className="badge">{squad.role}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="dashboard-section">
            <h2>QUICK ACTIONS</h2>
            <div className="actions-grid">
              <button className="action-button card" onClick={() => navigate('/squads')}>
                <div className="action-icon">👥</div>
                <h3>Manage Squads</h3>
                <p>Create or join gaming squads</p>
              </button>
              <button
                className="action-button card"
                onClick={async () => {
                  if (window.electronAPI) {
                    await window.electronAPI.captureScreenshot();
                  }
                }}
              >
                <div className="action-icon">📸</div>
                <h3>Test Screenshot</h3>
                <p>Press F9 or click here</p>
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default Dashboard;

